import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";

import LoginPage from "@/pages/Login";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import DashboardPage from "@/pages/Dashboard";
import ProfilePage from "@/pages/Profile";
import SettingsPage from "@/pages/Settings";
import PlaceholderModule from "@/pages/PlaceholderModule";
import PersonnelList from "@/pages/personnel/PersonnelList";
import PersonnelNew from "@/pages/personnel/PersonnelNew";
import PersonnelDetail from "@/pages/personnel/PersonnelDetail";
import DepartmentsList from "@/pages/departments/DepartmentsList";
import DepartmentDetail from "@/pages/departments/DepartmentDetail";
import RolesList from "@/pages/roles/RolesList";
import PermissionsCatalog from "@/pages/permissions/PermissionsCatalog";
import AuditLog from "@/pages/audit/AuditLog";
import CustomersList from "@/pages/customers/CustomersList";
import CustomerNew from "@/pages/customers/CustomerNew";
import CustomerDetail from "@/pages/customers/CustomerDetail";
import ExpensesPage from "@/pages/expenses/ExpensesPage";
import ShootPlannerPage from "@/pages/planner/ShootPlannerPage";
import CustomerPortal from "@/pages/portal/CustomerPortal";
import FinancePage from "@/pages/finance/FinancePage";
import ContentCalendarPage from "@/pages/content-calendar/ContentCalendarPage";
import ProjectsPage from "@/pages/projects/ProjectsPage";
import TasksKanbanPage from "@/pages/tasks/TasksKanbanPage";
import ProposalsPage from "@/pages/proposals/ProposalsPage";
import ContractsPage from "@/pages/contracts/ContractsPage";
import AiAssistantPage from "@/pages/ai-assistant/AiAssistantPage";
import TeamChatPage from "@/pages/team-chat/TeamChatPage";
import ReportsPage from "@/pages/reports/ReportsPage";

function App() {
    return (
        <div className="App">
            <ThemeProvider>
                <BrowserRouter>
                    <AuthProvider>
                        <Routes>
                            {/* Public */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                            {/* Customer Portal (client role only, no AppLayout) */}
                            <Route
                                path="/portal"
                                element={
                                    <ProtectedRoute allowClient onlyClient>
                                        <CustomerPortal />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Protected - internal users (non-client) */}
                            <Route
                                element={
                                    <ProtectedRoute>
                                        <AppLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                                <Route path="/dashboard" element={<DashboardPage />} />
                                <Route path="/profile" element={<ProfilePage />} />
                                <Route path="/settings" element={<SettingsPage />} />
                                <Route path="/personnel" element={<PersonnelList />} />
                                <Route path="/personnel/new" element={<PersonnelNew />} />
                                <Route path="/personnel/:id" element={<PersonnelDetail />} />
                                <Route path="/departments" element={<DepartmentsList />} />
                                <Route path="/departments/:id" element={<DepartmentDetail />} />
                                <Route path="/roles" element={<RolesList />} />
                                <Route path="/permissions" element={<PermissionsCatalog />} />
                                <Route path="/audit-log" element={<AuditLog />} />
                                <Route path="/customers" element={<CustomersList />} />
                                <Route path="/customers/new" element={<CustomerNew />} />
                                <Route path="/customers/:id" element={<CustomerDetail />} />
                                <Route path="/expenses" element={<ExpensesPage />} />
                                <Route path="/shoot-planner" element={<ShootPlannerPage />} />
                                <Route path="/finance" element={<FinancePage />} />
                                <Route path="/content-calendar" element={<ContentCalendarPage />} />
                                <Route path="/projects" element={<ProjectsPage />} />
                                <Route path="/tasks" element={<TasksKanbanPage />} />
                                <Route path="/proposals" element={<ProposalsPage />} />
                                <Route path="/contracts" element={<ContractsPage />} />
                                <Route path="/ai-assistant" element={<AiAssistantPage />} />
                                <Route path="/team-chat" element={<TeamChatPage />} />
                                <Route path="/reports" element={<ReportsPage />} />
                                <Route path="/module/:module" element={<PlaceholderModule />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                        <Toaster
                            position="top-right"
                            richColors
                            closeButton
                            toastOptions={{ style: { borderRadius: "12px" } }}
                        />
                    </AuthProvider>
                </BrowserRouter>
            </ThemeProvider>
        </div>
    );
}

export default App;
